import { AxiosError } from 'axios';
import { debounce, omit } from 'lodash-es';
import mitt, { type Emitter } from 'mitt';
import { makeAutoObservable, runInAction } from 'mobx';
import { isHydrated, makePersistable } from 'mobx-persist-store';

import {
  CatalogRequests,
  type ICatalogPrices,
  type ProductPropertiesKeyAPI,
  type RecommendationSource,
  type RecommendProductsSource,
  type SortingFieldName,
  type SortingOrder,
} from '~/api/Catalog';
import { FavoritesRequest } from '~/api/Favorites';
import type { ApiErrorResponse } from '~/api/Requests';
import { company } from '~/company/Company';
import type { AddToCartSource } from '~/services/analytics/sources';
import { mainStore, orderStore } from '~/stores';
import { type Category, Product } from '~/stores/CategoriesStore';
import { MetaStore } from '~/stores/shared/MetaStore';
import type { ProductPropertiesFilter } from '~/types/Product/interface';
import {
  catalogWarehouseParam,
  isClientError,
  requiredWarehouseCode,
} from '~/utils/catalogWarehouse';

import { PURCHASED_ITEMS_COUNT } from '../constants';
import { storage as localStorage } from '../LocalStorage';
import { NotifyMeStore } from '../NotifyMeStore';
import type { UserStore } from '../UserStore';
import type {
  Pagination,
  PriceMinMax,
  ProductFilters,
  ProductListValue,
  ProductPropertyFilterCode,
  ProductRangeFilter,
  ProductRecommendationResponse,
  ShutterColumn,
} from './interfaces';

export const PAGE_SIZE = 48;
export const STANDARD_DELIVERY_ITEMS_AVAILABLE = 10;

export class CatalogStore {
  purchasedItems = new Map<number, Product>();
  purchasedItemsPage = 0;
  savedItemsPage = 1;
  isFetchingSavedItems = false;
  // FIXME: save only product ID instead of whole object
  favorites: Record<string, Product> = {};
  recommendItems: Product[] = [];

  filters: ProductPropertiesFilter[] = [];
  currentFilters: ProductFilters = {
    properties: {},
  };
  selectedSorting: Nullable<[SortingFieldName, SortingOrder]> = null;
  currentBadges: string[] = [];
  categoryShutter: [boolean, number | string] = [false, 0];
  categoryShutterData: Record<number | string, ShutterColumn[]> = {};
  pricesminmax: PriceMinMax = {};
  feedbackProduct: Product | null = null;
  page: Pagination = {
    current: 1,
    size: PAGE_SIZE,
    total: 0,
  };
  productRecommendations: ProductRecommendationResponse = {};

  selectedCatalogProduct: string | null = null;
  /** Surface the SKU modal was opened from; the modal adds to cart on its behalf */
  selectedCatalogProductSource: AddToCartSource | null = null;

  readonly notifyMeStore: NotifyMeStore;
  readonly productRecommendationsMeta: MetaStore = new MetaStore();

  readonly applyFiltersEventEmitter: Emitter<{ submit: undefined }> = mitt<{
    submit: undefined;
  }>();

  constructor(protected readonly userStore: UserStore) {
    this.notifyMeStore = new NotifyMeStore();

    makeAutoObservable(this);
    makePersistable(this, {
      name: 'CatalogStore',
      properties: [
        'favorites',
        'currentFilters',
        'selectedSorting',
        'currentBadges',
      ],
      storage: localStorage,
    }).catch((error) => error && console.error(error));
  }

  // Getters
  get isSynchronized(): boolean {
    return isHydrated(this);
  }

  get defaultSorting(): [SortingFieldName, SortingOrder] {
    return company.is('localee') ? ['name', 'ASC'] : ['salesQuantity', 'DESC'];
  }

  get currentSorting(): [SortingFieldName, SortingOrder] {
    return this.selectedSorting ?? this.defaultSorting;
  }

  get favoritesList(): Product[] {
    const favoritesKeys = Object.keys(this.favorites);
    if (!favoritesKeys.length) {
      return [];
    }
    return Object.values(this.favorites);
  }

  get purchasedItemsList(): Product[] {
    if (!this.purchasedItems.size) {
      return [];
    }
    return Array.from(this.purchasedItems, ([, value]) => value);
  }

  // Setters
  setSelectedCatalogProduct(
    productId: string | null,
    source: AddToCartSource | null = null,
  ) {
    this.selectedCatalogProduct = productId;
    this.selectedCatalogProductSource = source;
  }

  setIsFetchingSavedItems(val: boolean) {
    this.isFetchingSavedItems = val;
  }

  setPrices(data?: ICatalogPrices) {
    // Converted-price companies (DubaiExpress) get sale-currency (RUB) bounds
    // from the backend; fall back to the base-currency range when they're
    // absent. `||` on purpose: the backend sends 0 (not null) in these fields
    // for companies without converted prices.
    const priceMin =
      data?.convertedDiscountPriceMin || data?.salePriceMin || data?.priceMin;
    const priceMax =
      data?.convertedDiscountPriceMax || data?.salePriceMax || data?.priceMax;
    this.pricesminmax = {
      priceMin,
      priceMax,
      priceMinPounds: Math.floor((priceMin || 0) / 100),
      priceMaxPounds: Math.ceil((priceMax || 0) / 100),
    };
  }

  setFilters(val: ProductPropertiesFilter[]) {
    this.filters = val;
  }

  setRecommendItems(products: Product[]) {
    this.recommendItems = products;
  }

  setFavorites(obj: Record<string, Product>) {
    this.favorites = obj;
  }

  setPagination(val: Pagination) {
    this.page = val;
  }

  public getProductFilters(
    filters?: ProductFilters,
    sorting?: [SortingFieldName, SortingOrder],
    page?: number,
  ) {
    const excluded: ProductPropertiesKeyAPI[] = ['type'];
    const properties = Object.entries(filters?.properties ?? {}) as [
      ProductPropertiesKeyAPI,
      any,
    ][];

    return {
      page: {
        size: this.page.size,
        current: page ?? this.page.current,
      },
      price: this.convertPriceFilterToPences(filters?.price),
      discountPrice: this.convertPriceFilterToPences(filters?.discountPrice),
      properties: properties.length
        ? {
            ...Object.fromEntries(
              properties.filter((p) => !excluded.includes(p[0])),
            ),
          }
        : {},
      sort: sorting ? [{ field: sorting[0], order: sorting[1] }] : undefined,
      ...(this.currentFilters.properties.type
        ? {
            subCategoryId: this.currentFilters.properties.type?.map(
              (i: string) => i.split('-').at(-1),
            ),
          }
        : {}),
    };
  }

  async requestProductRecommendations(
    skus: string[],
    sources: RecommendationSource[],
  ): Promise<ProductRecommendationResponse> {
    if (!skus.length || !sources.length) {
      return {};
    }

    try {
      const responses = await Promise.all(
        sources.map((source) =>
          CatalogRequests.getRecommendations({
            skus,
            ...catalogWarehouseParam(),
            source,
          }),
        ),
      );

      return sources.reduce<ProductRecommendationResponse>(
        (memo, source, idx) => {
          const response = responses[idx];

          if (!response) {
            return memo;
          }

          const { data, pagination } = response;

          return {
            ...memo,
            [source]: {
              data: data.map((product) => new Product(product)),
              pagination,
            },
          };
        },
        {},
      );
    } catch (error) {
      if (error instanceof AxiosError) {
        this.errorHandler(error, 'recommendations');
      }
      return {};
    }
  }

  async getProductRecommendations(
    skus: string[],
    sources: RecommendationSource[],
  ): Promise<void> {
    this.productRecommendationsMeta.setLoading(true);

    const recommendations = await this.requestProductRecommendations(
      skus,
      sources,
    );

    runInAction(() => {
      this.productRecommendations = {
        ...this.productRecommendations,
        ...recommendations,
      };

      this.productRecommendationsMeta.setLoading(false);
    });
  }

  readonly getProductRecommendationDebounced = debounce(
    this.getProductRecommendations.bind(this),
    350,
  );

  clearProductRecommendations(source: RecommendationSource): void {
    if (source in this.productRecommendations) {
      this.productRecommendations = omit(this.productRecommendations, source);
    }
  }

  async requestFilters() {
    try {
      const { data } = await CatalogRequests.getFilters();
      this.setFilters(data);

      return data;
    } catch (e) {
      await this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'requestFilters',
      );
      return [];
    }
  }

  async requestRecommendProducts(
    sku: string[],
    source: RecommendProductsSource,
  ): Promise<Product[] | null> {
    try {
      const { data } = await CatalogRequests.getRecommendProducts(
        orderStore.etaWarehouseCode,
        {
          sku,
          source,
          isSafeRequest: mainStore.isSafeRequest || undefined,
        },
      );
      const products: Product[] = [];
      data.forEach((item) => {
        const product = new Product(item);
        if (!product) {
          return;
        }
        products.push(product);
      });
      if (source === 'purchased_items') {
        this.setRecommendItems(products);
      }
      return products;
    } catch {
      return null;
    }
  }

  async fetchFavorites() {
    if (!this.userStore.isAuthorized || this.isFetchingSavedItems) {
      return;
    }
    this.setIsFetchingSavedItems(true);
    try {
      const { data } = await FavoritesRequest.getFavorites({
        ...catalogWarehouseParam(),
        isSafeRequest: mainStore.isSafeRequest || undefined,
        page: {
          current: this.savedItemsPage,
          size: 10,
        },
      });
      if (!data?.length) {
        return false;
      }
      const forEntries = data
        .map<[number, Product | null]>((product) => [
          product.id,
          new Product(product),
        ])
        .filter(([, product]) => product) as [number, Product][];
      this.setFavorites({
        ...this.favorites,
        ...Object.fromEntries(forEntries),
      });
      return true;
    } catch (e) {
      await this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'fetchFavorites',
      );
    } finally {
      this.setIsFetchingSavedItems(false);
    }
  }

  reset() {
    this.purchasedItems = new Map<number, Product>();
    this.purchasedItemsPage = 0;
  }

  async fetchPurchasedItems(isForce?: boolean): Promise<boolean> {
    if (!this.userStore.isAuthorized) {
      return false;
    }

    try {
      if (isForce) {
        /**
         * Code below will reset uploaded items
         *
         * It is specific case for situation when user makes an order, and was navigated to home page,
         * On main page user should see at the beginning of carousel products that was bought in last order.
         *
         * To achieve that we need to reset all loaded purchase items and fetch it again.
         * */
        this.purchasedItemsPage = 1;
        this.purchasedItems = new Map<number, Product>();
      } else {
        this.purchasedItemsPage += 1;
      }

      const { data = [] } = await this.requestBoughtItems(
        this.purchasedItemsPage,
        PURCHASED_ITEMS_COUNT,
      );

      runInAction(() => {
        data.forEach((product) => {
          const incoming = new Product(product);
          const existing = this.purchasedItems.get(product.id);
          this.purchasedItems.set(
            product.id,
            existing && company.hasNewLogistics
              ? this.mergePurchasedVariants(existing, incoming)
              : incoming,
          );
        });
      });

      return data?.length > 0;
    } catch (e) {
      e && console.error(e);
    }

    return false;
  }

  /**
   * LMS companies ask for stock across warehouses, so no code is sent; a
   * backend that still demands one is answered with the default code on retry,
   * which keeps the list alive whichever side is released first.
   */
  private async requestBoughtItems(page: number, limit: number) {
    const params = { ...catalogWarehouseParam(), page, limit };
    try {
      return await FavoritesRequest.getPurchasedItems(params);
    } catch (error) {
      if (params.warehouseCode || !isClientError(error)) {
        throw error;
      }
      return FavoritesRequest.getPurchasedItems({
        ...params,
        warehouseCode: requiredWarehouseCode(),
      });
    }
  }

  /**
   * `bought-items` lists one entry per purchased variant, so a product bought
   * in two colours arrives twice under one id and the later copy used to win
   * the id-keyed map. LMS cards open on a variant in stock, so the copies are
   * folded into one product carrying every purchased variant.
   */
  private mergePurchasedVariants(into: Product, from: Product): Product {
    const known = new Set(into.offers.map((offer) => offer.sku));
    Object.assign(into, {
      offers: [
        ...into.offers,
        ...from.offers.filter((offer) => !known.has(offer.sku)),
      ],
      sellable: Math.max(into.sellable, from.sellable),
    });
    return into;
  }

  async requestPurchasedItems(limit: number): Promise<Product[]> {
    if (!this.userStore.isAuthorized) {
      return [];
    }

    try {
      const { data = [] } = await this.requestBoughtItems(1, limit);

      const products = data.map((item) => new Product(item));
      if (!company.hasNewLogistics) {
        return products;
      }
      const byId = new Map<number, Product>();
      for (const product of products) {
        const existing = byId.get(product.id);
        byId.set(
          product.id,
          existing ? this.mergePurchasedVariants(existing, product) : product,
        );
      }
      return [...byId.values()];
    } catch (e) {
      e && console.error(e);
    }

    return [];
  }

  setCurrentFilters = (filters: ProductFilters) => {
    this.currentFilters = filters;
  };

  applyCatalogFilter(code: ProductPropertyFilterCode, value?: any) {
    this.page.current = 1;
    if (code === 'price' || code === 'discountPrice') {
      if (!value) {
        delete this.currentFilters[code];
      } else {
        this.currentFilters = {
          ...this.currentFilters,
          [code]: value,
        };
      }

      return;
    }

    /**
     * FYI: for backend puproses.
     *  it's required 3 state checkbox (no value, true, false) to able handle such cases for toggle type properties (Mark E.)
     * What is mean 'false' value for toggle property?
     *  - strict check false value?
     *  - property not added to product / offer?
     */

    if (!value || (typeof value === 'boolean' && !value)) {
      delete this.currentFilters.properties[code];
      return;
    }

    let propertyValue = value;

    if (Array.isArray(value)) {
      propertyValue = value.filter((i) => !!i || i === 0);
      if (value.length === 0) {
        delete this.currentFilters.properties[code];
        this.currentFilters = { ...this.currentFilters };
        return;
      }
    }

    this.currentFilters = {
      ...this.currentFilters,
      properties: {
        ...this.currentFilters.properties,
        [code]: propertyValue,
      },
    };
  }

  resetCatalog() {
    runInAction(() => {
      this.resetCatalogBadges();
      this.resetCatalogFilters();
      this.resetSorting();
    });
  }

  resetCatalogFilters() {
    this.currentFilters = {
      properties: {},
    };
  }

  applyCatalogSorting(sort: [SortingFieldName, SortingOrder]) {
    this.selectedSorting = sort;
  }

  addCatalogBadge(id: string, code: ProductPropertyFilterCode, value: any) {
    if (this.currentBadges.includes(id)) {
      return this.currentBadges;
    }

    this.currentBadges.push(id);
    this.applyCatalogFilter(code, value);
  }

  removeCatalogBadge(id: string, code: ProductPropertyFilterCode) {
    this.currentBadges = this.currentBadges.filter((b) => b !== id);
    this.applyCatalogFilter(code);
  }

  resetCatalogBadges() {
    this.currentBadges = [];
  }

  // Errors
  errorHandler = (
    error: AxiosError<ApiErrorResponse>,
    context: string,
  ): Promise<AxiosError> => mainStore.errorHandler(error, context);

  getSortProductsFn() {
    const sortingKey = this.currentSorting.join('.');

    switch (sortingKey) {
      case 'price.ASC':
        return (a: Product, b: Product) => a.price - b.price;
      case 'price.DESC':
        return (a: Product, b: Product) => b.price - a.price;
      case 'createdAt.DESC':
        return (a: Product, b: Product) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'salesQuantity.DESC':
        return (a: Product, b: Product) => b.salesQuantity - a.salesQuantity;
      case 'rating.DESC':
        return (a: Product, b: Product) => a.ratingAverage - b.ratingAverage;
    }
  }

  prepareProducts(category: Category): Product[] {
    const fn = this.getSortProductsFn();
    const type = this.currentFilters.properties?.['type'] as string;
    const subcategories = type
      ? category.subcategory.filter(({ id }) => type.includes(String(id)))
      : null;

    const getNestedProducts = (category: Category): [Product[], unknown[]] => [
      category.ownProducts,
      category.subcategory.map(getNestedProducts),
    ];

    const products = (
      subcategories?.length
        ? subcategories.map(getNestedProducts).flat(Infinity)
        : getNestedProducts(category).flat(Infinity)
    ) as Product[];

    return fn ? products.sort(fn) : products;
  }

  resetSorting() {
    this.selectedSorting = null;
  }

  setCategoryShutterData(category: ProductListValue) {
    this.categoryShutterData[category.categoryId] = category.filters
      .filter(({ code }) => ['type', 'region', 'country'].includes(code))
      .map(({ code, type, values }) => ({
        header: `properties:${code}`,
        categoryId: category.categoryId,
        items: values.map(({ label, value }) => ({
          label,
          filter: {
            code,
            value: type === 'selector' ? [value] : value,
          },
        })),
      }));
  }

  getCurrentCategoryShutterData() {
    return this.categoryShutterData[this.categoryShutter[1]];
  }

  openCategoryShutter(categoryId: string | number) {
    this.categoryShutter = [true, categoryId];
  }

  closeCategoryShutter() {
    this.categoryShutter = [false, 0];
  }

  private convertPriceFilterToPences(
    filter?: ProductRangeFilter,
  ): ProductRangeFilter | undefined {
    if (!filter) {
      return;
    }

    return {
      from: filter.from
        ? mainStore.convertPoundsToPence(filter.from)
        : filter.from,
      to: filter.to ? mainStore.convertPoundsToPence(filter.to) : filter.to,
    };
  }

  destroy() {
    // Implement this method to fall within the interface
  }
}
